import { useEffect, useRef, useState, useCallback } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Logo from '../../components/brand/Logo.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import LanguageSwitch from '../../components/ui/LanguageSwitch.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'
import StepCompany from './steps/StepCompany.jsx'
import StepLogo from './steps/StepLogo.jsx'
import StepBank from './steps/StepBank.jsx'
import StepNumbering from './steps/StepNumbering.jsx'
import StepPeppol from './steps/StepPeppol.jsx'
import StepCustomer from './steps/StepCustomer.jsx'
import StepDone from './steps/StepDone.jsx'

/*
 * Post-login company setup — inventory §17. A guided full-screen flow shown when
 * a company's setup isn't complete (gate in AppLayout redirects here). Reuses
 * the same building blocks as the settings/admin screens. Each step exposes a
 * submit() via `bindSubmit`; "Suivant" runs it and advances on success.
 */
const STEPS = [
  { key: 'company', Comp: StepCompany, skippable: false },
  { key: 'logo', Comp: StepLogo, skippable: true },
  { key: 'bank', Comp: StepBank, skippable: false },
  { key: 'numbering', Comp: StepNumbering, skippable: false },
  { key: 'peppol', Comp: StepPeppol, skippable: false },
  { key: 'customer', Comp: StepCustomer, skippable: true },
  { key: 'done', Comp: StepDone, skippable: false },
]

export default function SetupPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, loading } = useAuth()
  const { currentCompanyId } = useCompany()
  const { confirm } = useFeedback()
  const [i, setI] = useState(0)
  const [busy, setBusy] = useState(false)
  const submitRef = useRef(null)
  const bindSubmit = useCallback((fn) => { submitRef.current = fn }, [])

  useEffect(() => { window.scrollTo({ top: 0 }) }, [i])

  if (loading) return null
  if (!isAuthenticated) return <Navigate to="/login" replace />

  const step = STEPS[i]
  const isLast = i === STEPS.length - 1
  const next = async () => {
    setBusy(true)
    try {
      const ok = submitRef.current ? await submitRef.current() : true
      if (ok !== false) setI((n) => Math.min(n + 1, STEPS.length - 1))
    } finally { setBusy(false) }
  }
  const skipStep = () => setI((n) => Math.min(n + 1, STEPS.length - 1))

  const finish = async () => {
    setBusy(true)
    try { await api.post('/onboarding/complete') } catch { /* proceed anyway */ }
    finally { window.location.assign('/app/dashboard') }
  }

  const skipAll = async () => {
    const ok = await confirm({ tone: 'info', destructive: false, title: t('setup.skipAllTitle'), message: t('setup.skipAllMessage'), confirmLabel: t('setup.skipAll'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.post('/onboarding/skip') } catch { /* ignore */ }
    navigate('/app/dashboard')
  }

  const StepComp = step.Comp
  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg-page)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 28px', borderBottom: '1px solid var(--color-border)', background: '#fff' }}>
        <Logo size={25} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <LanguageSwitch />
          <button onClick={skipAll} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>{t('setup.skipAll')}</button>
        </div>
      </header>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 24px 40px' }}>
          {/* progress */}
          <div style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, color: 'var(--color-ink)' }}>{t(`setup.steps.${step.key}.title`)}</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{i + 1} / {STEPS.length}{step.skippable && <span style={{ marginLeft: 8, color: 'var(--color-text-muted)' }}>· {t('setup.optional')}</span>}</span>
            </div>
            <div style={{ height: 6, borderRadius: 4, background: 'var(--color-border)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${((i + 1) / STEPS.length) * 100}%`, background: 'var(--color-primary)', transition: 'width .25s ease' }} />
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-card-lg)', boxShadow: 'var(--shadow-card)', padding: '26px 28px', animation: 'vfade .2s ease' }}>
            <StepComp companyId={currentCompanyId} bindSubmit={bindSubmit} onFinish={finish} />
          </div>

          {!isLast && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
              <Button variant="secondary" disabled={i === 0 || busy} onClick={() => setI((n) => Math.max(0, n - 1))}>{t('setup.back')}</Button>
              <div style={{ display: 'flex', gap: 10 }}>
                {step.skippable && <Button variant="secondary" disabled={busy} onClick={skipStep}>{t('setup.skip')}</Button>}
                <Button variant="primary" icon="chevron-right" loading={busy} onClick={next}>{t('setup.next')}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
